import { defined } from '@platform/schema';
import { sceneTeaches } from '@platform/schema/content';
import {
  SCENE_SEEDS,
  sceneBoundsRefusal,
} from '@platform/schema/publish-rules';

import { moved } from '../ordering/moved';

import type { CreateSceneData } from '@platform/client';
import type { ArcChange } from '@platform/schema/chat';
import type { AuthorableKind } from '@platform/schema/content';

/**
 * Which scenes a lesson has, and in what order — GEN-45.
 *
 * The editor could change a scene's words and neither its neighbours nor its place. The
 * API has replaced the placement list wholesale since `GEN-08`; what was missing is a
 * caller that hands it a different list.
 *
 * Pure, and separate from the form for the reason `edits.ts` gives: the rules about how
 * many scenes a lesson may have belong beside each other rather than inside a component.
 */

export type SceneSeed = NonNullable<CreateSceneData['body']>;

/**
 * Widened for the reason `edits.ts` widens `sceneRole`: the SDK renders `kind` as a plain
 * string, and narrowing here would need a cast at every call site.
 */
const TEACHES: Record<string, boolean> = sceneTeaches;

/** Whether the run plays this kind at all — a REWARD does not, nor does a kind newer
 * than this build, which is what the widening above answers for. */
export function teaches(kind: string): boolean {
  return TEACHES[kind] === true;
}

/** How many of these scenes count against the bound. Rows are not the count — CURR-12. */
export function teachingScenes(scenes: readonly { kind: string }[]): number {
  let count = 0;
  for (const scene of scenes) if (teaches(scene.kind)) count += 1;

  return count;
}

/** Why another scene cannot be added, or nothing. */
export function addable(teaching: number): string | undefined {
  return sceneBoundsRefusal(teaching, teaching + 1);
}

/** Why this scene cannot be removed, or nothing. */
export function removable(teaching: number): string | undefined {
  return sceneBoundsRefusal(teaching, teaching - 1);
}

/** A scene of this kind, ready to be edited rather than assembled. */
export function seedFor(kind: AuthorableKind): SceneSeed {
  const { items, ...rest } = SCENE_SEEDS[kind];

  // `defined` for the reason `sceneSeed` gives below: the schema's optional keys are not the DTO's.
  return defined({
    kind,
    ...rest,
    ...(items === undefined ? {} : { items: items.map(defined) }),
  });
}

/**
 * A scene the planner wrote, as the SDK's create body — GEN-77.
 *
 * `defined` and not a cast, and applied to the items too, because it is shallow: Zod infers
 * `key?: T | undefined` where the DTO under `exactOptionalPropertyTypes` is `key?: T`, and
 * an option carries four of those. Dropping the absent keys is also what the body wants —
 * a `correctAmount: undefined` on a CHOICE option is a column its mechanic does not read.
 */
export function sceneSeed(
  scene: Extract<ArcChange, { op: 'add' }>['scene'],
): SceneSeed {
  const { items, ...rest } = scene;

  return defined({
    ...rest,
    ...(items === undefined ? {} : { items: items.map(defined) }),
  });
}

/** As much of a scene as placing one and counting it need: which it is, and what kind. */
export interface Placed {
  id: string;
  kind: string;
}

/** Where an `add` puts the scene — where it asked, or last. One default, two readers. */
export const addedAt = (position: number | undefined, held: number): number =>
  position ?? held + 1;

/**
 * The scenes a lesson holds after this change, in order — GEN-77.
 *
 * One projection and not two: the write reads the ids off it and the bounds warning counts
 * the kinds on it, so what each operation *means* is stated once. A second switch over the
 * count was a disagreement waiting to happen, and a silent one.
 *
 * `createdId` is the one thing this cannot know — `add` and `retype` need a scene to exist
 * before it is placed. The *kind* is on the change, which is what lets the count be taken
 * before the POST. A change naming a scene the lesson does not hold leaves the list alone,
 * which `proposedChanges` has already refused.
 */
export function arcAfter(
  change: ArcChange,
  current: readonly Placed[],
  createdId = '',
): Placed[] {
  if (change.op === 'remove')
    return current.filter((scene) => scene.id !== change.sceneId);

  if (change.op === 'move') {
    const from = current.findIndex((scene) => scene.id === change.sceneId);

    return from === -1
      ? [...current]
      : moved(current, from, change.position - 1);
  }

  const made: Placed = { id: createdId, kind: change.scene.kind };

  if (change.op === 'retype')
    return current.map((scene) => (scene.id === change.sceneId ? made : scene));

  const next = [...current];
  next.splice(addedAt(change.position, next.length) - 1, 0, made);

  return next;
}
